export function resolveSpxThemeMode() {
  if (typeof document === 'undefined') {
    return 'light';
  }

  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
}

function updateThemeToggles(theme) {
  if (typeof document === 'undefined') {
    return;
  }

  const lightIcon = `
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M12 3v2.2M12 18.8V21M4.93 4.93l1.56 1.56M17.51 17.51l1.56 1.56M3 12h2.2M18.8 12H21M4.93 19.07l1.56-1.56M17.51 6.49l1.56-1.56"/>
      <circle cx="12" cy="12" r="4.25"/>
    </svg>
  `;
  const darkIcon = `
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M20.2 14.4A8.8 8.8 0 1 1 9.6 3.8a7 7 0 0 0 10.6 10.6z"/>
    </svg>
  `;

  document.querySelectorAll('[data-spx-theme-toggle]').forEach((toggle) => {
    toggle.setAttribute('aria-pressed', theme === 'light' ? 'true' : 'false');
    toggle.innerHTML = theme === 'light' ? darkIcon : lightIcon;
  });
}

export function setSpxThemeMode(theme) {
  if (typeof document === 'undefined') {
    return;
  }

  document.documentElement.setAttribute('data-theme', theme);

  try {
    window.localStorage.setItem('schoolplex-theme', theme);
  } catch (error) {
    // Ignore storage failures in constrained browser contexts.
  }

  updateThemeToggles(theme);
  syncAutoLogos();
}

function bindThemeToggles() {
  if (typeof document === 'undefined') {
    return;
  }

  document.querySelectorAll('[data-spx-theme-toggle]').forEach((toggle) => {
    if (toggle.dataset.spxThemeBound === 'true') {
      return;
    }

    toggle.dataset.spxThemeBound = 'true';
    toggle.addEventListener('click', () => {
      const nextTheme = resolveSpxThemeMode() === 'light' ? 'dark' : 'light';
      setSpxThemeMode(nextTheme);
    });
  });
}

function syncAutoLogos() {
  if (typeof document === 'undefined') {
    return;
  }

  const isDark = resolveSpxThemeMode() === 'dark';

  document.querySelectorAll('[data-spx-logo-tone="auto"]').forEach((logo) => {
    const defaultSrc = logo.getAttribute('data-spx-logo-default-src');
    const inverseSrc = logo.getAttribute('data-spx-logo-inverse-src');

    if (!defaultSrc || !inverseSrc) {
      return;
    }

    logo.setAttribute('src', isDark ? inverseSrc : defaultSrc);
  });
}

function dismissNode(button) {
  const mode = button.getAttribute('data-spx-dismiss');

  if (mode === 'closest') {
    const target = button.closest('.spx-alert, .spx-banner, .spx-toast');

    if (target) {
      target.remove();
    }

    return;
  }

  if (!mode) {
    return;
  }

  const target = document.querySelector(mode);

  if (target) {
    target.remove();
  }
}

function bindDismissActions() {
  if (typeof document === 'undefined') {
    return;
  }

  document.querySelectorAll('[data-spx-dismiss]').forEach((button) => {
    if (button.dataset.spxDismissBound === 'true') {
      return;
    }

    button.dataset.spxDismissBound = 'true';
    button.addEventListener('click', () => dismissNode(button));
  });
}

function setDrawerState(drawer, isOpen) {
  if (!drawer) {
    return;
  }

  const drawerId = drawer.getAttribute('id');
  const backdrop = drawerId ? document.querySelector(`[data-spx-drawer-backdrop="${drawerId}"]`) : null;

  drawer.classList.toggle('is-open', isOpen);
  drawer.setAttribute('aria-hidden', isOpen ? 'false' : 'true');

  if (backdrop) {
    backdrop.classList.toggle('is-open', isOpen);
  }

  if (isOpen) {
    drawer.focus();
  }
}

function bindDrawers() {
  if (typeof document === 'undefined') {
    return;
  }

  document.querySelectorAll('[data-spx-drawer-toggle]').forEach((toggle) => {
    if (toggle.dataset.spxDrawerBound === 'true') {
      return;
    }

    toggle.dataset.spxDrawerBound = 'true';
    toggle.addEventListener('click', () => {
      const selector = toggle.getAttribute('data-spx-drawer-toggle');
      const drawer = selector ? document.querySelector(selector) : null;

      if (!drawer) {
        return;
      }

      const shouldOpen = !drawer.classList.contains('is-open');
      setDrawerState(drawer, shouldOpen);
    });
  });

  document.querySelectorAll('[data-spx-drawer-close]').forEach((button) => {
    if (button.dataset.spxDrawerCloseBound === 'true') {
      return;
    }

    button.dataset.spxDrawerCloseBound = 'true';
    button.addEventListener('click', () => {
      const drawerId = button.getAttribute('data-spx-drawer-close');
      const drawer = drawerId ? document.getElementById(drawerId) : null;

      if (drawer) {
        setDrawerState(drawer, false);
      }
    });
  });

  document.querySelectorAll('[data-spx-drawer-backdrop]').forEach((backdrop) => {
    if (backdrop.dataset.spxDrawerBackdropBound === 'true') {
      return;
    }

    backdrop.dataset.spxDrawerBackdropBound = 'true';
    backdrop.addEventListener('click', () => {
      const drawerId = backdrop.getAttribute('data-spx-drawer-backdrop');
      const drawer = drawerId ? document.getElementById(drawerId) : null;

      if (drawer) {
        setDrawerState(drawer, false);
      }
    });
  });

  if (document.body?.dataset.spxDrawerEscapeBound !== 'true') {
    document.body.dataset.spxDrawerEscapeBound = 'true';

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') {
        return;
      }

      document.querySelectorAll('[data-spx-drawer].is-open').forEach((drawer) => {
        setDrawerState(drawer, false);
      });
    });
  }
}

function activateTab(tab) {
  const tabList = tab.closest('[data-spx-tab-list]');

  if (!tabList) {
    return;
  }

  tabList.querySelectorAll('[data-spx-tab-target]').forEach((candidate) => {
    const isActive = candidate === tab;
    const targetSelector = candidate.getAttribute('data-spx-tab-target');
    const panel = targetSelector ? document.querySelector(targetSelector) : null;

    candidate.setAttribute('aria-selected', isActive ? 'true' : 'false');

    if (panel) {
      if (isActive) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    }
  });
}

function bindTabs() {
  if (typeof document === 'undefined') {
    return;
  }

  document.querySelectorAll('[data-spx-tab-list]').forEach((tabList) => {
    const tabs = Array.from(tabList.querySelectorAll('[data-spx-tab-target]'));

    tabs.forEach((tab) => {
      if (tab.dataset.spxTabBound === 'true') {
        return;
      }

      tab.dataset.spxTabBound = 'true';
      tab.addEventListener('click', () => activateTab(tab));
    });

    const activeTab = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0];

    if (activeTab) {
      activateTab(activeTab);
    }
  });
}

export function onSpxReady(callback) {
  if (typeof document === 'undefined') {
    return;
  }

  document.addEventListener('spx:ready', callback);
}

export function initSpxUi() {
  if (typeof document === 'undefined') {
    return;
  }

  updateThemeToggles(resolveSpxThemeMode());
  syncAutoLogos();
  bindThemeToggles();
  bindDismissActions();
  bindDrawers();
  bindTabs();

  document.dispatchEvent(new CustomEvent('spx:ready', {
    detail: {
      theme: resolveSpxThemeMode()
    }
  }));
}
